import {debounce} from '@github/mini-throttle'
import {useChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {makeRepositoryReference} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {CopilotChatRepo} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {testIdProps} from '@github-ui/test-id-props'
import {ActionList} from '@primer/react'
import {SelectPanel} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {type RefObject, useEffect, useMemo, useState} from 'react'
import {flushSync} from 'react-dom'

import {useRepositoryItems} from '../hooks/use-repository-items'
import {group} from '../utils/array'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {DelayedComponent} from '../utils/DelayedComponent'
import {TopicPickerPortalContainer} from './PortalContainerUtils'
import styles from './RepoSelectPanel.module.css'
import {RepositoryListItem} from './RepositoryListItem'

type RepoReferencesSelectPanelProps = Omit<
  RepoSelectPanelProps,
  'selectedRepoIds' | 'onSelectRepo' | 'selectionVariant' | 'description' | 'error'
> & {
  onRepoSelect?: (repo: CopilotChatRepo) => void
}

/** Select one or more repos to use as references (attachments). */
export function RepoReferencesSelectPanel(props: RepoReferencesSelectPanelProps) {
  const {currentReferences} = useChatState()
  const manager = useChatManager()

  const selectedRepoReferences = currentReferences.filter(r => r.type === 'repository')
  const selectedRepoIds = new Set(selectedRepoReferences.map(r => r.id))

  const [error, setError] = useState<string>()

  const toggleRepoReference = async (repoId: number, queryLength: number) => {
    setError(undefined)

    const selectedReference = selectedRepoReferences.find(r => r.id === repoId)
    if (selectedReference) {
      manager.removeReference(selectedReference)
      sendEvent('dotcom_chat.activate', {
        target: 'ATTACHMENT_MENU_REPOSITORIES',
        action: 'removed',
        component: 'ATTACHMENT_MENU',
        mode: 'immersive',
      })
      return
    }

    const repoResponse = await manager.service.fetchRepo(repoId)
    if (!repoResponse.ok) {
      setError('Error: failed to attach repository. Please try again.')
      props.onOpenChange(true)
      return
    }

    props.onRepoSelect?.(repoResponse.payload)
    const reference = makeRepositoryReference(repoResponse.payload)
    manager.addReference(reference, 'repoMenu')
    sendEvent('dotcom_chat.activate', {
      target: 'ATTACHMENT_MENU_REPOSITORIES',
      action: 'selected',
      component: 'ATTACHMENT_MENU',
      mode: 'immersive',
      queryLength,
    })
  }

  return (
    <RepoSelectPanel
      {...props}
      selectionVariant="multiple"
      selectedRepoIds={selectedRepoIds}
      onSelectRepo={toggleRepoReference}
      description="Choose repositories to chat about."
      error={error}
    />
  )
}

export interface RepoSelectPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  submitReturnFocusRef: RefObject<HTMLElement | null>
  cancelReturnFocusRef: RefObject<HTMLElement | null>
  selectedRepoIds: Set<number>
  /** Repos to feature at the top. Defaults to selected repos. */
  featuredRepoIds?: Set<number>
  onSelectRepo: (repoId: number, queryLength: number) => Promise<void>
  selectionVariant: 'instant' | 'multiple'
  description?: string
  error?: string
  ownerDisplayLogin?: string
  includeForks?: boolean // false by default
  anchorRef?: RefObject<HTMLButtonElement | null>
  /**
   * Flag for the new repo search behavior where user repositories are
   * searched first if the search query starts with the current user's login.
   */
  searchUserReposFirst?: boolean // false by default
}

export function RepoSelectPanel({
  open,
  onOpenChange,
  submitReturnFocusRef,
  cancelReturnFocusRef,
  selectedRepoIds,
  featuredRepoIds = selectedRepoIds,
  onSelectRepo,
  selectionVariant,
  description,
  error,
  ownerDisplayLogin,
  includeForks = false,
  anchorRef,
}: RepoSelectPanelProps) {
  const {ssoOrganizations} = useChatState()

  const [filter, setFilter] = useState('')

  // Don't send off new filter queries if the user is typing
  const debouncedSetFilter = useMemo(
    () => debounce((newFilter: string) => setFilter(newFilter), 150, {start: false}),
    [],
  )
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!open) setFilter('')
  }, [open])
  // ownerDisplayLogin and includeForks are currently only used by Spaces to filter repositories by the owner for org-owned spaces.

  const result = useRepositoryItems(filter, open, undefined, ownerDisplayLogin, includeForks)
  const {repositories, loading: reposLoading} = result

  const srRepoSearchStatus = useMemo(() => {
    if (reposLoading) return 'Loading repositories'
    if (repositories.length === 0) return 'No repositories found'
    if (repositories.length === 1) return '1 repository found'
    return `${repositories.length} repositories found`
  }, [reposLoading, repositories.length])

  const onSearchInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    debouncedSetFilter(event.currentTarget.value)
  }

  const ssoOrgNames = useMemo(() => {
    if (!ssoOrganizations) return []
    return ssoOrganizations.map(org => org.login)
  }, [ssoOrganizations])

  // Move featured repos to the top
  const {featuredRepos, nonFeaturedRepos} = group(repositories, r =>
    featuredRepoIds.has(r.databaseId) ? 'featuredRepos' : 'nonFeaturedRepos',
  )

  const [selecting, setSelecting] = useState(false)
  const onSelect = async (repoId: number) => {
    setSelecting(true)
    await onSelectRepo(repoId, filter.length)
    setSelecting(false)
  }

  return (
    <div {...testIdProps('repo-select-panel')}>
      <SelectPanel
        className={clsx(anchorRef && styles.CopilotRepoSelectPanelAnchored)}
        width="large"
        maxHeight="xlarge"
        onCancel={() => {
          // eslint-disable-next-line @eslint-react/dom-no-flush-sync
          flushSync(() => onOpenChange(false))
          cancelReturnFocusRef.current?.focus()
          sendEvent('dotcom_chat.activate', {target: 'REPOSITORY_DIALOG_CLOSE', mode: 'immersive'})
        }}
        onSubmit={() => {
          // eslint-disable-next-line @eslint-react/dom-no-flush-sync
          flushSync(() => onOpenChange(false))
          submitReturnFocusRef.current?.focus()
        }}
        open={open}
        variant={anchorRef ? 'anchored' : 'modal'}
        selectionVariant={selectionVariant}
        title={selectionVariant === 'multiple' ? 'Select repositories' : 'Select a repository'}
        description={description}
        anchorRef={anchorRef}
      >
        <SelectPanel.Header className={clsx(ssoOrgNames.length > 0 && 'border-bottom-0')}>
          <SelectPanel.SearchInput
            loading={reposLoading === true || selecting}
            onChange={onSearchInputChange}
            placeholder="Search repositories"
            aria-label="Search repositories"
          />
        </SelectPanel.Header>
        <TopicPickerPortalContainer />
        {error && (
          <SelectPanel.Message variant="error" size="inline">
            {error}
          </SelectPanel.Message>
        )}
        {reposLoading === 'initial' ? (
          <div className={styles.ActionList}>
            <div className="sr-only">{srRepoSearchStatus}</div>
            <SelectPanel.Loading>Fetching repositories&hellip;</SelectPanel.Loading>
          </div>
        ) : repositories.length === 0 && !reposLoading ? (
          <div className={styles.ActionList}>
            <div className="sr-only">{srRepoSearchStatus}</div>
            <div className={styles.emptyStateMessage}>
              <DelayedComponent delay={750}>
                <SelectPanel.Message variant="empty" title="No repositories found">
                  Try a different search term
                </SelectPanel.Message>
              </DelayedComponent>
            </div>
          </div>
        ) : (
          <ActionList className={styles.ActionList}>
            <div className="sr-only">{srRepoSearchStatus}</div>
            {featuredRepos?.map(repo => (
              <RepositoryListItem
                key={repo.databaseId}
                repo={repo}
                onSelect={onSelect}
                selected={selectedRepoIds.has(repo.databaseId)}
              />
            ))}
            {featuredRepos && nonFeaturedRepos && <ActionList.Divider />}
            {nonFeaturedRepos?.map(repo => (
              <RepositoryListItem
                key={repo.databaseId}
                repo={repo}
                onSelect={onSelect}
                selected={selectedRepoIds.has(repo.databaseId)}
              />
            ))}
          </ActionList>
        )}
      </SelectPanel>
    </div>
  )
}
